import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import axios from 'axios';

import API_BASE from '../../utils/api';
const API = API_BASE;

// The unified salary Records list includes non-teaching-staff records
// (generated from the separate Non-Teaching Staff page) alongside teacher/
// principal ones — this must cover all three employeeRole values, not just
// principal-vs-everything-else, or non-teaching staff show up mislabeled.
const ROLE_LABELS = {
  principal: { label: 'Principal', className: 'bg-purple-100 text-purple-700' },
  class_teacher: { label: 'Teacher', className: 'bg-blue-100 text-blue-700' },
  non_teaching_staff: { label: 'Non-Teaching Staff', className: 'bg-teal-100 text-teal-700' },
};
const roleBadge = (role) => ROLE_LABELS[role] || { label: role || 'Unknown', className: 'bg-gray-100 text-gray-700' };

const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const Salary = () => {
  const [staffList, setStaffList] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('generate');
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [message, setMessage] = useState({ type: '', text: '' });
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [form, setForm] = useState({
    employeeId: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    salaryMode: 'attendance',
    fullMonthPay: '',
    hra: '',
    da: '',
    allowances: '',
    deductions: '',
    tax: '',
    performanceRating: '',
    performanceBonus: '',
    remarks: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/salary/staff-list`, { headers });
      setStaffList(res.data);
    } catch (err) {
      console.error('Error fetching staff:', err);
    }
  }, []);

  const fetchSalaries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/salary`, {
        headers,
        params: { month: filterMonth, year: filterYear }
      });
      setSalaries(res.data);
    } catch (err) {
      console.error('Error fetching salaries:', err);
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterYear]);

  const fetchAttendanceStats = useCallback(async (empId, month, year) => {
    if (!empId) { setAttendanceStats(null); return; }
    try {
      setLoadingStats(true);
      const res = await axios.get(`${API}/salary/attendance-stats/${empId}`, {
        headers,
        params: { month, year }
      });
      setAttendanceStats(res.data);
    } catch (err) {
      console.error('Error fetching attendance:', err);
      setAttendanceStats(null);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  useEffect(() => {
    if (activeTab === 'records') fetchSalaries();
  }, [activeTab, fetchSalaries]);

  // Fetch attendance when employee or month/year changes
  useEffect(() => {
    if (form.employeeId && form.month && form.year) {
      fetchAttendanceStats(form.employeeId, form.month, form.year);
    } else {
      setAttendanceStats(null);
    }
  }, [form.employeeId, form.month, form.year, fetchAttendanceStats]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const getBasicPay = () => {
    const fullPay = parseFloat(form.fullMonthPay) || 0;
    if (form.salaryMode === 'attendance' && attendanceStats && attendanceStats.totalWorkingDays > 0) {
      return Math.round((fullPay / attendanceStats.totalWorkingDays) * attendanceStats.daysPresent);
    }
    return fullPay;
  };

  const calculateNet = () => {
    const basic = getBasicPay();
    const hra = parseFloat(form.hra) || 0;
    const da = parseFloat(form.da) || 0;
    const allow = parseFloat(form.allowances) || 0;
    const deduct = parseFloat(form.deductions) || 0;
    const tax = parseFloat(form.tax) || 0;
    const bonus = form.salaryMode === 'performance' ? (parseFloat(form.performanceBonus) || 0) : 0;
    return basic + hra + da + allow + bonus - deduct - tax;
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!form.employeeId || !form.fullMonthPay) {
      showMessage('error', 'Please select employee and enter full month pay');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        employeeId: form.employeeId,
        month: form.month,
        year: form.year,
        salaryMode: form.salaryMode,
        fullMonthPay: parseFloat(form.fullMonthPay),
        basicPay: getBasicPay(),
        hra: parseFloat(form.hra) || 0,
        da: parseFloat(form.da) || 0,
        allowances: parseFloat(form.allowances) || 0,
        deductions: parseFloat(form.deductions) || 0,
        tax: parseFloat(form.tax) || 0,
        totalWorkingDays: attendanceStats?.totalWorkingDays || 0,
        daysPresent: attendanceStats?.daysPresent || 0,
        daysAbsent: attendanceStats?.daysAbsent || 0,
        remarks: form.remarks
      };
      if (form.salaryMode === 'performance') {
        payload.performanceRating = parseInt(form.performanceRating) || undefined;
        payload.performanceBonus = parseFloat(form.performanceBonus) || 0;
      }
      await axios.post(`${API}/salary/generate`, payload, { headers });
      showMessage('success', 'Salary generated successfully!');
      setForm({ ...form, employeeId: '', fullMonthPay: '', hra: '', da: '', allowances: '', deductions: '', tax: '', performanceRating: '', performanceBonus: '', remarks: '' });
      setAttendanceStats(null);
      fetchSalaries();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to generate salary');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (id, paymentMode) => {
    try {
      await axios.put(`${API}/salary/${id}/pay`, { paymentMode }, { headers });
      showMessage('success', 'Salary marked as paid!');
      fetchSalaries();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to mark as paid');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this salary record?')) return;
    try {
      await axios.delete(`${API}/salary/${id}`, { headers });
      showMessage('success', 'Salary record deleted');
      fetchSalaries();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to delete');
    }
  };

  const selectedEmployee = staffList.find(s => s._id === form.employeeId);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">💰 Salary Management</h1>
              <p className="text-gray-500 mt-1">Generate and manage staff salaries</p>
            </div>
            <ProfileDropdown />
          </div>

          {/* Message */}
          {message.text && (
            <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
              {message.text}
            </div>
          )}

          {/* Tabs */}
          <div className="flex space-x-1 bg-gray-200 rounded-lg p-1 mb-6 w-fit">
            <button
              onClick={() => setActiveTab('generate')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'generate' ? 'bg-white text-indigo-700 shadow' : 'text-gray-600 hover:text-gray-800'}`}
            >
              Generate Salary
            </button>
            <button
              onClick={() => setActiveTab('records')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'records' ? 'bg-white text-indigo-700 shadow' : 'text-gray-600 hover:text-gray-800'}`}
            >
              Salary Records
            </button>
          </div>

          {/* Generate Tab */}
          {activeTab === 'generate' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Generate Pay Slip</h2>
              <form onSubmit={handleGenerate}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Employee Select */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                    <select
                      value={form.employeeId}
                      onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    >
                      <option value="">Select Employee</option>
                      <optgroup label="Principal">
                        {staffList.filter(s => s.role === 'principal').map(s => (
                          <option key={s._id} value={s._id}>{s.name} ({s.employeeId || 'N/A'})</option>
                        ))}
                      </optgroup>
                      <optgroup label="Teachers">
                        {staffList.filter(s => s.role === 'class_teacher').map(s => (
                          <option key={s._id} value={s._id}>
                            {s.name} ({s.employeeId || 'N/A'}) {s.classId ? `- ${s.classId.className} ${s.classId.section}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {/* Month */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month *</label>
                    <select
                      value={form.month}
                      onChange={(e) => setForm({ ...form, month: parseInt(e.target.value) })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>

                  {/* Year */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                    <input
                      type="number"
                      value={form.year}
                      onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      min="2020" max="2099"
                    />
                  </div>
                </div>

                {/* Selected employee info */}
                {selectedEmployee && (
                  <div className="bg-indigo-50 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-indigo-500 text-white rounded-full flex items-center justify-center text-lg font-bold">
                        {selectedEmployee.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{selectedEmployee.name}</p>
                        <p className="text-sm text-gray-500">{selectedEmployee.email} | {selectedEmployee.role === 'principal' ? 'Principal' : 'Teacher'} | ID: {selectedEmployee.employeeId || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Attendance Stats */}
                {selectedEmployee && (
                  <div className="mb-6">
                    {loadingStats ? (
                      <div className="text-center py-4 text-gray-500">Loading attendance...</div>
                    ) : attendanceStats ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                          <p className="text-xl font-bold text-blue-700">{attendanceStats.totalWorkingDays}</p>
                          <p className="text-xs text-blue-600">Working Days</p>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                          <p className="text-xl font-bold text-green-700">{attendanceStats.daysPresent}</p>
                          <p className="text-xs text-green-600">Days Present</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                          <p className="text-xl font-bold text-red-700">{attendanceStats.daysAbsent}</p>
                          <p className="text-xs text-red-600">Days Absent</p>
                        </div>
                        <div className={`border rounded-lg p-3 text-center ${attendanceStats.attendancePercentage >= 80 ? 'bg-green-50 border-green-200' : attendanceStats.attendancePercentage >= 60 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                          <p className={`text-xl font-bold ${attendanceStats.attendancePercentage >= 80 ? 'text-green-700' : attendanceStats.attendancePercentage >= 60 ? 'text-yellow-700' : 'text-red-700'}`}>{attendanceStats.attendancePercentage}%</p>
                          <p className="text-xs text-gray-600">Attendance %</p>
                        </div>
                      </div>
                    ) : null}
                    {attendanceStats && (
                      <p className="text-xs text-gray-500 mt-2">
                        🗓️ {attendanceStats.holidayDays || 0} declared holiday{attendanceStats.holidayDays === 1 ? '' : 's'} excluded from working days
                        {' · '}🌴 {attendanceStats.leaveDays || 0} approved leave day{attendanceStats.leaveDays === 1 ? '' : 's'} counted as paid (not deducted from pay)
                      </p>
                    )}
                  </div>
                )}

                {/* Salary Mode Toggle */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Salary Mode</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setForm({ ...form, salaryMode: 'attendance' })}
                      className={`px-5 py-2 rounded-lg font-medium transition-colors ${form.salaryMode === 'attendance' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      📋 Attendance Based
                    </button>
                    <button type="button" onClick={() => setForm({ ...form, salaryMode: 'performance' })}
                      className={`px-5 py-2 rounded-lg font-medium transition-colors ${form.salaryMode === 'performance' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      ⭐ Performance Based
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {form.salaryMode === 'attendance'
                      ? 'Basic pay is prorated based on days present out of working days'
                      : 'Full month pay + admin sets performance rating and bonus'}
                  </p>
                </div>

                {/* Pay Breakdown */}
                <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">Pay Breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Month Pay (₹) *</label>
                    <input type="number" value={form.fullMonthPay} onChange={(e) => setForm({ ...form, fullMonthPay: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="0" required min="0" />
                  </div>
                  {form.salaryMode === 'attendance' && attendanceStats && form.fullMonthPay && (
                    <div className="flex items-end">
                      <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-2 w-full">
                        <p className="text-xs text-amber-700">Prorated Basic Pay</p>
                        <p className="text-lg font-bold text-amber-800">₹{getBasicPay().toLocaleString('en-IN')}</p>
                        <p className="text-xs text-amber-600">({attendanceStats.daysPresent}/{attendanceStats.totalWorkingDays} days)</p>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">HRA (₹)</label>
                    <input type="number" value={form.hra} onChange={(e) => setForm({ ...form, hra: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="0" min="0" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">DA (₹)</label>
                    <input type="number" value={form.da} onChange={(e) => setForm({ ...form, da: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="0" min="0" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Other Allowances (₹)</label>
                    <input type="number" value={form.allowances} onChange={(e) => setForm({ ...form, allowances: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="0" min="0" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deductions (₹)</label>
                    <input type="number" value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="0" min="0" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax (₹)</label>
                    <input type="number" value={form.tax} onChange={(e) => setForm({ ...form, tax: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="0" min="0" />
                  </div>
                </div>

                {/* Performance fields */}
                {form.salaryMode === 'performance' && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-purple-800 mb-3">⭐ Performance Evaluation</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rating (1-5)</label>
                        <select value={form.performanceRating} onChange={(e) => setForm({ ...form, performanceRating: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2">
                          <option value="">Select Rating</option>
                          <option value="1">1 - Poor</option>
                          <option value="2">2 - Below Average</option>
                          <option value="3">3 - Average</option>
                          <option value="4">4 - Good</option>
                          <option value="5">5 - Excellent</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Performance Bonus (₹)</label>
                        <input type="number" value={form.performanceBonus} onChange={(e) => setForm({ ...form, performanceBonus: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          placeholder="0" min="0" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Remarks */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows="2" placeholder="Any remarks..." />
                </div>

                {/* Net Pay Preview */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg p-4 mb-6 flex justify-between items-center">
                  <span className="text-lg font-medium">Net Pay</span>
                  <span className="text-3xl font-bold">₹{calculateNet().toLocaleString('en-IN')}</span>
                </div>

                <button type="submit" disabled={loading}
                  className="bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 font-medium">
                  {loading ? 'Generating...' : 'Generate Salary'}
                </button>
              </form>
            </div>
          )}

          {/* Records Tab */}
          {activeTab === 'records' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              {/* Filters */}
              <div className="flex flex-wrap gap-4 items-end mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select value={filterMonth} onChange={(e) => setFilterMonth(parseInt(e.target.value))}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <input type="number" value={filterYear} onChange={(e) => setFilterYear(parseInt(e.target.value))}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-28 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    min="2020" max="2099" />
                </div>
                <button onClick={fetchSalaries}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors">
                  🔍 Search
                </button>
              </div>

              {/* Summary Cards */}
              {salaries.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-700">{salaries.length}</p>
                    <p className="text-sm text-blue-600">Total Records</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-700">{salaries.filter(s => s.status === 'paid').length}</p>
                    <p className="text-sm text-green-600">Paid</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-yellow-700">{salaries.filter(s => s.status === 'generated').length}</p>
                    <p className="text-sm text-yellow-600">Pending</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-purple-700">
                      ₹{salaries.reduce((sum, s) => sum + (s.netPay || 0), 0).toLocaleString('en-IN')}
                    </p>
                    <p className="text-sm text-purple-600">Total Amount</p>
                  </div>
                </div>
              )}

              {/* Salary Table */}
              {loading ? (
                <div className="text-center py-10 text-gray-500">Loading...</div>
              ) : salaries.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <p className="text-4xl mb-2">📭</p>
                  <p>No salary records for {months.find(m => m.value === filterMonth)?.label} {filterYear}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="p-3 text-sm font-semibold text-gray-600">#</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Employee</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Role</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Basic</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">HRA</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">DA</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Allowances</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Deductions</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Tax</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Net Pay</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Status</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaries.map((s, i) => (
                        <tr key={s._id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-sm">{i + 1}</td>
                          <td className="p-3">
                            <p className="font-medium text-gray-800">{s.employeeId?.name}</p>
                            <p className="text-xs text-gray-500">{s.employeeId?.email}</p>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleBadge(s.employeeRole).className}`}>
                              {roleBadge(s.employeeRole).label}
                            </span>
                          </td>
                          <td className="p-3 text-sm">₹{s.basicPay?.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-sm">₹{s.hra?.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-sm">₹{s.da?.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-sm">₹{s.allowances?.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-sm text-red-600">-₹{s.deductions?.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-sm text-red-600">-₹{s.tax?.toLocaleString('en-IN')}</td>
                          <td className="p-3 font-bold text-green-700">₹{s.netPay?.toLocaleString('en-IN')}</td>
                          <td className="p-3">
                            {s.status === 'paid' ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">✅ Paid</span>
                            ) : s.status === 'cancelled' ? (
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">❌ Cancelled</span>
                            ) : (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">⏳ Generated</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              {s.status === 'generated' && (
                                <>
                                  <select
                                    onChange={(e) => { if (e.target.value) handleMarkPaid(s._id, e.target.value); }}
                                    className="text-xs border rounded px-2 py-1"
                                    defaultValue=""
                                  >
                                    <option value="" disabled>Pay via...</option>
                                    <option value="bank_transfer">Bank Transfer</option>
                                    <option value="cheque">Cheque</option>
                                    <option value="cash">Cash</option>
                                    <option value="upi">UPI</option>
                                  </select>
                                  <button onClick={() => handleDelete(s._id)}
                                    className="text-red-500 hover:text-red-700 text-xs px-2 py-1 border border-red-300 rounded hover:bg-red-50">
                                    🗑️
                                  </button>
                                </>
                              )}
                              {s.status === 'paid' && s.paidDate && (
                                <span className="text-xs text-gray-500">
                                  {new Date(s.paidDate).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Salary;
